import { Suspense, lazy, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Calendar } from "lucide-react";
import { formatDateOnly } from "@/lib/format";
import { cn } from "@/lib/cn";
import "./DateInput.css";

// The calendar body pulls in react-day-picker + date-fns locales + its
// stylesheet. Load it lazily so a *closed* trigger (often many per page) stays
// cheap to mount and the heavy bundle only evaluates when a picker is opened.
const DateInputCalendar = lazy(() => import("./DateInputCalendar"));

interface Props {
  /**
   * The current value as an ISO `YYYY-MM-DD` string, or `""` when empty. This is
   * the exact wire format the API/query string expects — it never changes.
   */
  value: string;
  /**
   * Fired with the new ISO `YYYY-MM-DD` string (or `""` when cleared). The
   * emitted value is always ISO regardless of the displayed locale format.
   */
  onChange: (iso: string) => void;
  /** Forwarded to the trigger button for `<label htmlFor>` association. */
  id?: string;
  /** Disabled state — mirrors a native disabled date input. */
  disabled?: boolean;
  /** Accessible label when no visible `<label htmlFor>` is wired. */
  "aria-label"?: string;
  /** Optional ISO `YYYY-MM-DD` lower bound (days before it are disabled). */
  min?: string;
  /** Optional ISO `YYYY-MM-DD` upper bound (days after it are disabled). */
  max?: string;
  /** Overridable placeholder shown when empty. Defaults to `common.datePlaceholder`. */
  placeholder?: string;
  /**
   * Visual variant of the trigger:
   * - `"box"` (default): a self-contained bordered field with a calendar icon —
   *   a drop-in replacement for a standalone `<input type="date">`.
   * - `"bare"`: a borderless, transparent, iconless trigger for use inside a
   *   pre-styled filter group that already provides the border and icon
   *   (e.g. the Routes/Orders/Analytics date-range boxes).
   */
  variant?: "box" | "bare";
  /** Extra Tailwind classes merged onto the trigger. */
  className?: string;
}

const BOX_TRIGGER =
  "flex h-10 w-full items-center gap-2 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 outline-none transition-colors focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400";

const BARE_TRIGGER =
  "flex items-center bg-transparent text-sm text-gray-900 outline-none disabled:cursor-not-allowed disabled:text-gray-400";

/** Popover panel width used when clamping it inside the viewport. */
const PANEL_WIDTH = 300;
/**
 * Approximate popover HEIGHT, used to decide whether the panel fits below the
 * trigger or must flip above it (BUG-512). A real measurement is impossible
 * before the panel renders, so this is deliberately generous — a month grid
 * with six week rows plus the header comes to ~330 px. Over-estimating flips
 * slightly early, which is harmless; under-estimating clips the calendar, which
 * is the defect this exists to prevent.
 */
const PANEL_HEIGHT = 340;

/**
 * Locale-aware date input (TASK-3909 → TASK-4441).
 *
 * The native `<input type="date">` renders its calendar popup (month names,
 * weekday headers, first-day-of-week) from the *browser/OS* locale, which no
 * React prop can localize — so under `ru` the picker stayed English on most
 * machines. This component replaces the native input with a `react-day-picker`
 * JS calendar localized via a `date-fns` locale (`ru` / `enGB`), guaranteeing
 * Russian month/weekday names under `ru` regardless of the OS.
 *
 * The visible trigger keeps the app's own locale-aware {@link formatDate}
 * display ("28 июн. 2026 г." / "Jun 28, 2026"). The wire contract is unchanged:
 * `value` in and the `onChange` payload out are always ISO `YYYY-MM-DD` (or `""`
 * when empty), so the ~15 call sites migrate by swapping the element, not
 * rewiring their state.
 *
 * The calendar is hosted in a MANUAL anchored `createPortal` panel (mirroring
 * `KpiConfiguratorDrawer`) — deliberately NOT a Radix portalled overlay, which
 * sets `pointer-events:none` on `<body>` and closes on interact-outside (a real
 * runtime bug this project has hit before). The panel fixes its position from
 * the trigger's `getBoundingClientRect()`, forces `pointer-events-auto`, and
 * closes on click-outside or Esc. The calendar body itself is lazy-loaded so a
 * closed trigger stays cheap.
 */
export function DateInput({
  value,
  onChange,
  id,
  disabled,
  "aria-label": ariaLabel,
  min,
  max,
  placeholder,
  variant = "box",
  className,
}: Props) {
  const { t, i18n } = useTranslation();
  const fallbackId = useId();
  const triggerId = id ?? fallbackId;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  /*
   * `formatDateOnly`, not `formatDate` (TASK-5043). `value` is documented above
   * as always ISO `YYYY-MM-DD`, and `formatDate` would parse that as UTC
   * midnight and render the PREVIOUS day in any negative-offset browser — the
   * field would display a different date than the one it holds and emits.
   * A zoned instant formatter would be just as wrong: there is no instant here,
   * only a calendar day the user picked.
   */
  const display = value ? formatDateOnly(value) : "";

  // Position the popover fixed to the trigger's bounding box on open.
  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8);

    // Vertical placement: FLIP above the trigger when the panel would not fit
    // below it (BUG-512). Only the horizontal axis was clamped before, so a
    // trigger low on the page — the «Дата доставки» row of the bulk-edit menu,
    // which sits near the bottom by construction — opened a calendar running
    // 97 px past the viewport, with its last weeks unreachable.
    //
    // `PANEL_HEIGHT` is an estimate, deliberately generous: the panel is not
    // measurable before it renders, and over-estimating flips a little eagerly
    // (harmless) while under-estimating clips (the bug). If neither side fits,
    // pin to the top edge and let the panel's own `max-height` scroll it, so
    // the days are always reachable.
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const top =
      spaceBelow >= PANEL_HEIGHT || spaceBelow >= spaceAbove
        ? Math.max(
            8,
            Math.min(rect.bottom + 6, window.innerHeight - PANEL_HEIGHT - 8),
          )
        : Math.max(8, rect.top - PANEL_HEIGHT - 6);

    setPos({ top, left: Math.max(8, left) });
  }, [open]);

  // Esc closes the popover.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function handleSelect(iso: string) {
    onChange(iso);
    // Picking a concrete day closes the popover; clearing keeps it open.
    if (iso) setOpen(false);
  }

  const placeholderText = placeholder ?? t("common.datePlaceholder");

  return (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          variant === "bare" ? BARE_TRIGGER : BOX_TRIGGER,
          className,
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left",
            display ? "text-gray-900" : "text-gray-400",
          )}
        >
          {display || placeholderText}
        </span>
        {variant === "box" && (
          <Calendar
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-gray-400"
            strokeWidth={2}
          />
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            {/* Click-outside backdrop — transparent, closes on outside click.
                pointer-events-auto so it actually receives the click. */}
            <div
              className="pointer-events-auto fixed inset-0 z-[1190]"
              onClick={() => setOpen(false)}
              aria-hidden="true"
              data-portal-popover="date-input"
            />
            <div
              ref={panelRef}
              role="dialog"
              aria-label={ariaLabel ?? placeholderText}
              // Fixed screen position from the trigger rect — legitimately
              // dynamic, so an inline top/left is unavoidable here.
              style={{ top: pos.top, left: pos.left }}
              // BUG-497: `data-portal-popover` marks this subtree as portalled
              // content that logically belongs to the trigger, even though
              // `createPortal` puts it under <body>. Ancestor popovers that
              // close themselves on outside-pointerdown (e.g.
              // BulkEditMenu) use `isInsidePortalPopover` to skip these
              // nodes — without it, clicking a calendar day reads as an outside
              // click and tears down the menu before the value is applied.
              data-portal-popover="date-input"
              className="date-input-popover pointer-events-auto fixed z-[1200] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-xl ring-1 ring-black/5"
            >
              <Suspense fallback={null}>
                <DateInputCalendar
                  value={value}
                  language={i18n.language}
                  min={min}
                  max={max}
                  onSelect={handleSelect}
                />
              </Suspense>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
